import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { get } from "../../api/api";
import { fetchCustomers } from "../../api/customers";
import { useCurrentProfile } from "../../ContextWrapper";
import AsyncSelect from "react-select/async";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// userType: 'ADMIN' | 'ACCOUNTANT' (passed from parent or context)
const QualityControlReviews = ({ userType }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const contextValue = useCurrentProfile();
  const profile =
    contextValue && contextValue.profile ? contextValue.profile : null;
  const navigate = useNavigate();
  const muiTheme = useMemo(() => createTheme(), []);

  const getDefaultYearMonth = () => {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return {
      year: lastMonth.getFullYear(),
      month: lastMonth.getMonth(), // 0-indexed
    };
  };
  const [selectedYearMonth, setSelectedYearMonth] = useState(
    getDefaultYearMonth()
  );
  const { year, month } = selectedYearMonth;

  const handleYearChange = (e) => {
    setSelectedYearMonth((prev) => ({
      ...prev,
      year: parseInt(e.target.value, 10),
    }));
  };
  const handleMonthChange = (e) => {
    setSelectedYearMonth((prev) => ({
      ...prev,
      month: parseInt(e.target.value, 10),
    }));
  };

  const openSnackbar = useCallback((message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  // Compute start and end date from selected year and month
  const startDate = dayjs(
    `${year}-${String(month + 1).padStart(2, "0")}-01`
  ).format("YYYY-MM-DD");
  const endDate = dayjs(startDate).endOf("month").format("YYYY-MM-DD");

  const renderStatusChip = (status) => {
    const key = (status || "").toLowerCase();
    const map = {
      completed: { bg: "#E6F9F2", color: "#1b7f4f" },
      in_progress: { bg: "#e7f0ff", color: "#2f6fca" },
      pending: { bg: "#fff8e1", color: "#b78103" },
      failed: { bg: "#fde7e7", color: "#b42318" },
      feedback_required: { bg: "#fde7d6", color: "#c56a1a" },
    };
    const token = map[key] || { bg: "#eef1f6", color: "#374151" };
    return (
      <Chip
        label={status || "-"}
        size="small"
        sx={{
          bgcolor: token.bg,
          color: token.color,
          fontWeight: 700,
          borderRadius: "10px",
          "& .MuiChip-label": { px: 1.5, textTransform: "uppercase" },
        }}
      />
    );
  };

  // Fetch reviews from API
  const fetchReviews = useCallback(async (searchValue = search) => {
    setLoading(true);
    try {
      const data = await get("/api/v2/qualitycontrolreviews/", {
        search: searchValue,
      });
      setReviews(data.results || data);
      setFilteredReviews(data.results || data);
    } catch (error) {
      openSnackbar("Failed to load reviews.", "error");
    } finally {
      setLoading(false);
    }
  }, [search, openSnackbar]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Fetch customers for the select (async search)
  const fetchCustomerOptions = useCallback(
    async (searchValue = "") => {
      if (profile) {
        const options = await fetchCustomers(
          profile,
          { search: searchValue },
          true
        );
        return options || [];
      }
      return [];
    },
    [profile]
  );

  const handleCustomerSelect = (option) => {
    setSelectedCustomer(option);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value || "";
    setSearch(value);
    if (value.trim() === "") {
      fetchReviews("");
    }
  };

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    fetchReviews();
  };

  const handleCreateReview = () => {
    setCreateError("");
    setSelectedCustomer(null);
    setCreateOpen(true);
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) {
      setCreateError("Please select a client.");
      return;
    }
    setCreating(true);
    setCreateError("");
    try {
      // Use a longer timeout for review creation (60s)
      const response = await axios.post(
        "/api/v2/qualitycontrolreviews/",
        {
          customer: selectedCustomer.value,
          start_date: startDate,
          end_date: endDate,
        },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
          timeout: 60000, // 60 seconds
        }
      );
      const newReview = response.data;
      // Defensive: Ensure backend returns the new review object with an id
      if (newReview && newReview.id) {
        openSnackbar("Review created", "success");
        navigate(`/quality-control-reviews/${newReview.id}`);
      } else {
        openSnackbar("Failed to get new review ID from server.", "error");
        setCreating(false);
      }
    } catch (error) {
      openSnackbar("Failed to create review.", "error");
      setCreating(false);
      setCreateOpen(false);
      fetchReviews();
    }
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Container maxWidth="lg" sx={{ pt: 10, pb: 6 }}>
      <Stack spacing={4}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: "1px solid #e5e7eb",
            boxShadow: "0 6px 14px rgba(0,0,0,0.06)",
          }}
        >
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md="auto">
              <Stack spacing={0.5}>
                <Typography variant="h5" fontWeight={700}>
                  Quality Control Review History
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View and manage quality control review history
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md>
              <Stack direction="row" spacing={2} justifyContent={{ xs: "flex-start", md: "flex-end" }} alignItems="center" flexWrap="wrap">
                <TextField
                  size="small"
                  placeholder="Search by client..."
                  value={search}
                  onChange={handleSearchChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearchSubmit(e);
                    }
                  }}
                  sx={{ minWidth: 260, bgcolor: "#f8fafc" }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleSearchSubmit} aria-label="search">
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={handleCreateReview}
                  sx={{ textTransform: "none", bgcolor: "#2973d6", "&:hover": { bgcolor: "#1f65c0" } }}
                >
                  New Review
                </Button>
              </Stack>
            </Grid>
          </Grid>

          <Box mt={3} sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#e9f0ff" }}>
                  <TableCell sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 12, color: "#54607a" }}>Review ID</TableCell>
                  <TableCell sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 12, color: "#54607a" }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 12, color: "#54607a" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 12, color: "#54607a" }}>Last Run</TableCell>
                  <TableCell sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 12, color: "#54607a" }}>Issues</TableCell>
                  <TableCell sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 12, color: "#54607a" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : reviews.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <Typography>No reviews found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredReviews.map((review) => (
                    <TableRow key={review.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{review.id}</TableCell>
                      <TableCell>{review.customer_name}</TableCell>
                      <TableCell>{renderStatusChip(review.status)}</TableCell>
                      <TableCell>{review.updated_on || review.created_on || "-"}</TableCell>
                      <TableCell>
                        {Array.isArray(review.issues)
                          ? review.issues.length
                          : review.feedback || "-"}
                        {!Array.isArray(review.issues) &&
                          !review.feedback && <span> - </span>}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          sx={{ textTransform: "none", borderColor: "#2f6fca", color: "#2f6fca" }}
                          onClick={() => navigate(`/quality-control-reviews/${review.id}`)}
                        >
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>

        <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
            Create New Review
            <IconButton aria-label="close" onClick={() => setCreateOpen(false)} sx={{ ml: "auto" }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <form onSubmit={handleSubmitCreate}>
            <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
              <FormControl fullWidth required sx={{ mb: 3 }}>
                <FormLabel>Client</FormLabel>
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={fetchCustomerOptions}
                  value={selectedCustomer}
                  onChange={handleCustomerSelect}
                  placeholder="Select client..."
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 20000 }),
                    control: (base) => ({
                      ...base,
                      minHeight: 56,
                      borderRadius: 8,
                    }),
                  }}
                  isClearable
                />
              </FormControl>
              <Stack spacing={2.5}>
                <FormControl fullWidth required>
                  <FormLabel>Year</FormLabel>
                  <Select
                    value={year}
                    onChange={handleYearChange}
                    size="medium"
                    sx={{ borderRadius: 2, minHeight: 56 }}
                  >
                    {Array.from({ length: 7 }, (_, i) => {
                      const y = new Date().getFullYear() - 3 + i;
                      return (
                        <MenuItem key={y} value={y}>
                          {y}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <FormControl fullWidth required>
                  <FormLabel>Month</FormLabel>
                  <Select
                    value={month}
                    onChange={handleMonthChange}
                    size="medium"
                    sx={{ borderRadius: 2, minHeight: 56 }}
                  >
                    {MONTHS.map((m, idx) => (
                      <MenuItem key={m} value={idx}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Review will cover: {startDate} to {endDate}
              </Typography>
              {createError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {createError}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCreateOpen(false)} variant="text">
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={creating}>
                {creating ? "Creating..." : "Create"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Container>
    </ThemeProvider>
  );
};

export default QualityControlReviews;
